import React, { useEffect, useReducer, useRef } from "react";
import PropTypes from "prop-types";
import RoundedArrow from "components/core/Icons/RoundedArrow";
import { Button } from "components/core/Button";

import "./ImageCarousel.scss";

export const ImageCarouselControl = ({ direction, onClick, isDisabled }) => (
	<Button
		type="button"
		fullWidth={false}
		disabled={isDisabled}
		className={`image-carousel-control ${direction}`}
		onClick={onClick}
		label={direction}
	>
		<RoundedArrow
			size="medium"
			fill="white"
			rotate={direction === "next" ? "right" : "left"}
		/>
	</Button>
);

ImageCarouselControl.propTypes = {
	direction: PropTypes.oneOf(["previous", "next"]).isRequired,
	isDisabled: PropTypes.bool,
	onClick: PropTypes.func.isRequired
};

export const ImageCarouselActions = {
	PREVIOUS: "PREVIOUS",
	NEXT: "NEXT",
	SET: "SET"
};

export const ImageCarouselControlReducer = (state, action) => {
	switch (action.type) {
		case ImageCarouselActions.PREVIOUS:
			// if the current image is index 0 don't do anything
			if (state.currentImageIndex === 0) {
				return state;
			}
			return {
				...state,
				currentImageIndex: state.currentImageIndex - 1
			};
		case ImageCarouselActions.NEXT:
			// if the current image index is the last image don't do anything
			if (state.currentImageIndex === state.imageCount - 1) {
				return state;
			}
			return {
				...state,
				currentImageIndex: state.currentImageIndex + 1
			};
		case ImageCarouselActions.SET:
			return {
				...state,
				currentImageIndex: action.index
			};
		default:
			return state;
	}
};

const ImageCarouselViewer = ({
	currentImage,
	atFirstImage,
	atLastImage,
	handlePrevious,
	handleNext
}) => (
	<div className="viewer">
		<ImageCarouselControl
			direction="previous"
			isDisabled={atFirstImage}
			onClick={handlePrevious}
		/>
		<div className="current-image">
			<img
				src={currentImage}
				alt="facility"
				width="600"
				height="400"
			/>
		</div>
		<ImageCarouselControl
			direction="next"
			isDisabled={atLastImage}
			onClick={handleNext}
		/>
	</div>
);

ImageCarouselViewer.propTypes = {
	currentImage: PropTypes.string.isRequired,
	atFirstImage: PropTypes.bool.isRequired,
	atLastImage: PropTypes.bool.isRequired,
	handlePrevious: PropTypes.func.isRequired,
	handleNext: PropTypes.func.isRequired
};

export const scrollThumbnailIntoView = (index, ref) => {
	/* istanbul ignore next */
	const thumbnail = ref.current.querySelector(
		`.thumbnail:nth-child(${index + 1})`
	);
	/* istanbul ignore next */
	// JSDom doesn't support `scrollIntoView` so we can't test this
	if (thumbnail.scrollIntoView) {
		thumbnail.scrollIntoView({
			behavior: "smooth",
			block: "nearest",
			inline: "nearest"
		});
	}
};

export const ImageCarouselThumbnails = ({
	images,
	currentImageIndex,
	handleSetImage
}) => {
	const scrollerRef = useRef(null);

	useEffect(() => {
		if (globalThis.location.hash !== "#reviews") {
			scrollThumbnailIntoView(currentImageIndex, scrollerRef);
		}
	}, [currentImageIndex]);

	return (
		<div className="thumbnail-picker">
			<div
				ref={scrollerRef}
				className="thumbnails-slider"
			>
				<div className="thumbnails">
					{images.map((image, index) => (
						<Button
							fullWidth={false}
							type="button"
							key={image}
							className={`thumbnail ${
								index === currentImageIndex ? "active" : ""
							}`}
							onClick={() => handleSetImage(index)}
						>
							<img
								src={image}
								alt="Facility"
							/>
						</Button>
					))}
				</div>
			</div>
		</div>
	);
};

ImageCarouselThumbnails.propTypes = {
	images: PropTypes.arrayOf(PropTypes.string).isRequired,
	currentImageIndex: PropTypes.number.isRequired,
	handleSetImage: PropTypes.func.isRequired
};

export const ImageCarousel = ({ images }) => {
	if (!images) return null;

	const [{ currentImageIndex }, dispatch] = useReducer(
		ImageCarouselControlReducer,
		{
			currentImageIndex: 0,
			imageCount: images.length
		}
	);

	const handleNext = () => dispatch({ type: ImageCarouselActions.NEXT });
	const handlePrevious = () =>
		dispatch({ type: ImageCarouselActions.PREVIOUS });
	const handleSetImage = (index) =>
		dispatch({ type: ImageCarouselActions.SET, index });

	return (
		<div className="image-carousel">
			{/* IMAGE AREA */}
			<ImageCarouselViewer
				currentImage={images[currentImageIndex]}
				atFirstImage={currentImageIndex === 0}
				atLastImage={currentImageIndex === images.length - 1}
				handleNext={handleNext}
				handlePrevious={handlePrevious}
			/>
			{/* THUMBNAILS */}
			<ImageCarouselThumbnails
				images={images}
				currentImageIndex={currentImageIndex}
				handleSetImage={handleSetImage}
			/>
		</div>
	);
};

ImageCarousel.propTypes = {
	images: PropTypes.arrayOf(PropTypes.string).isRequired
};
